import {useEffect, useMemo} from 'react';

import {SpinnerContainer} from '@deviceinsight/ng-ui-basic-components';

import {KpiError, OutdatedConfigError} from '../common/components/kpiWidgetErrors';
import EditModeOverlay from '../../EditModeOverlay';
import './KpiSingleValueWidget.scss';
import useMultipleKpiValues from '../common/hooks/useMultipleKpiValues';
import {KpiSingleValueWidgetConfigSettings, KpiSingleValueWidgetProps} from './types';
import {AggregationSettings} from '../common/types';
import {UPDATE_WIDGET_INTERVAL} from '../common/kpiUtils';
import {Period} from '@deviceinsight/ng-ui-components';
import {useUserAccountInfo, WidgetProps} from '@deviceinsight/ng-ui-scale-lib/api';

const createAggregationSettings = (
	context: WidgetProps['context'],
	settings: KpiSingleValueWidgetConfigSettings,
	dashboardTimePeriod: Period | null | undefined
): AggregationSettings => {
	const urn = context.type === 'asset' ? context.asset.urn : settings.assetUrn;
	const items =
		settings.kpiName && urn
			? [
					{
						kpiName: settings.kpiName,
						assetUrn: urn
					}
			  ]
			: [];
	return {
		items,
		aggregationType: settings.aggregationType,
		query: {
			periodTimeframe: dashboardTimePeriod ? 'CUSTOM' : settings.periodTimeframe,
			periodType: settings.periodType,
			periodCount: settings.periodCount,
			customPeriodFrom: dashboardTimePeriod ? dashboardTimePeriod.tsStart : settings.customPeriodFrom,
			customPeriodTo: dashboardTimePeriod ? dashboardTimePeriod.tsEnd : settings.customPeriodTo,
			workingShiftEnabled: settings.workingShiftEnabled,
			workingShiftDays: settings.workingShiftDays,
			workingShiftTimeFrom: settings.workingShiftTimeFrom,
			workingShiftTimeTo: settings.workingShiftTimeTo
		}
	};
};

const KpiSingleValueWidget = ({
	context,
	config: {
		title,
		key,
		settings,
		settings: {kpiName, unit, color, subtitle, periodTimeframe, maximumFractionDigits}
	},
	editMode,
	dashboardTime,
	dashboardPeriod
}: KpiSingleValueWidgetProps) => {
	const aggregationSettings = useMemo(
		() => createAggregationSettings(context, settings, dashboardPeriod),
		[context, settings, dashboardPeriod]
	);
	const {values, fetching, errorMessage, lastUpdate, reload} = useMultipleKpiValues(
		context,
		aggregationSettings,
		key,
		dashboardTime,
		editMode
	);
	useEffect(() => {
		const timeoutId = setTimeout(reload, UPDATE_WIDGET_INTERVAL);
		return () => clearTimeout(timeoutId);
	}, [reload, lastUpdate]);

	const {locale} = useUserAccountInfo();

	if (periodTimeframe) {
		return (
			<SpinnerContainer show={fetching && !errorMessage} size={30} data-testid="spinner-wrapper">
				<div
					className="dash-box kpi-single-value"
					style={{
						border: `1px solid ${color || ''}`
					}}>
					<div className="dash-box-head-container" data-testid="widget-title">
						<div className="dash-box-head">
							<span
								style={{
									color: color || undefined
								}}>
								{title}
							</span>
						</div>
					</div>
					{errorMessage ? (
						<KpiError kpiName={kpiName || ''} errorMessage={errorMessage} />
					) : (
						<div
							className="content"
							style={{
								color: color || undefined
							}}
							data-testid="content">
							<span className="value" data-testid="value">
								{!values.length || isNaN(values[0]?.value ?? NaN) || values[0].value == null
									? '--'
									: new Intl.NumberFormat(locale, {
											maximumFractionDigits: maximumFractionDigits ?? 10
									  }).format(values[0].value)}
							</span>
							<span className="unit" data-testid="unit">
								{unit}
							</span>
						</div>
					)}
					{subtitle && (
						<div className="dash-box-footline">
							<span
								style={{
									color: color || undefined
								}}>
								{subtitle}
							</span>
						</div>
					)}
					{editMode && <EditModeOverlay />}
				</div>
			</SpinnerContainer>
		);
	} else {
		return <OutdatedConfigError />;
	}
};

export default KpiSingleValueWidget;
